/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ContactDeliveryMethod, QuotationPrintMethod } from '@gh/builder-shared-data';
import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  isEntityInvalid,
  queryVersion,
  setEntityFields,
  Table,
  whenModified,
} from '@gh/common-shared';
import {
  EntityVersion,
  ID,
  PrintEntryPoint,
  WithDirty,
  WithFormMode,
  WithRecalculationNeeded,
  WithVersion,
} from '@gh/common-shared-data';
import { FormMode, isCreateFormMode, isEditFormMode } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { flow, isEmpty } from 'lodash';
import {
  QuoteDetailsBottomInfo,
  QuoteDetailsDerivedInfo,
  QuoteDetailsInitResponse,
  QuoteDetailsMasterInfo,
  QuoteDetailsSaveRequest,
  QuotePrintFilter,
  QuotePrintRequest,
  QuoteVariation,
  quoteVariationTable,
  validateQuoteMasterInfoForCreate,
  validateQuoteMasterInfoForEdit,
} from '../../resources/quote.model';

export enum QuoteDetailsStateEventType {
  Load = 'Load',
  MergeOnSave = 'MergeOnSave',
  MergeOnRecalculate = 'MergeOnRecalculate',
  SetMasterInfoShadow = 'SetMasterInfoShadow',
  CommitMasterInfo = 'CommitMasterInfo',
  VariationAddSet = 'VariationAddSet',
  VariationSetField = 'VariationSetField',
  VariationRemove = 'VariationRemove',
  SetBottomField = 'SetBottomField',
  VariationMoveCreditDebit = 'VariationMoveCreditDebit',
  NavigateToInvalid = 'NavigateToInvalid',
  NavigateToNotPriceBookVariation = 'NavigateToNotPriceBookVariation',
  ResetFocusedCell = 'ResetFocusedCell',
  Approved = 'Approved',
  Unapproved = 'Unapproved',
  UpdateVersion = 'UpdateVersion',
  SetProfitVisibility = 'SetProfitVisibility',
  ChangeVariationListFilter = 'ChangeVariationListFilter',
}

export type QuoteDetailsStateLoadEvent = {
  type: QuoteDetailsStateEventType.Load;
  init: QuoteDetailsInitResponse;
  supportsProfit: boolean;
  isProfitVisible: boolean;
  mode?: FormMode;
};

export type QuoteDetailsStateMergeOnSaveEvent = {
  type: QuoteDetailsStateEventType.MergeOnSave;
  saveResponse: QuoteDetailsInitResponse;
};

export type QuoteDetailsStateMergeOnRecalculateEvent = {
  type: QuoteDetailsStateEventType.MergeOnRecalculate;
  saveResponse: QuoteDetailsInitResponse;
};

export type QuoteDetailsStateSetMasterInfoShadowEvent = {
  type: QuoteDetailsStateEventType.SetMasterInfoShadow;
  snapshot: FormStateSnapshot<QuoteDetailsMasterInfo>;
  derivedInfo?: QuoteDetailsDerivedInfo;
};

export type QuoteDetailsStateCommitMasterInfoEvent = {
  type: QuoteDetailsStateEventType.CommitMasterInfo;
  resetVariations?: boolean;
};

export type QuoteDetailsStateVariationAddSetEvent = {
  type: QuoteDetailsStateEventType.VariationAddSet;
  variations: QuoteVariation[];
};

export type QuoteDetailsStateVariationSetFieldEvent = {
  type: QuoteDetailsStateEventType.VariationSetField;
  variationId: ID;
  name: string;
  value: any;
};

export type QuoteDetailsStateVariationRemoveEvent = {
  type: QuoteDetailsStateEventType.VariationRemove;
  variationId: ID;
};

export type QuoteDetailsStateSetBottomFieldEvent = {
  type: QuoteDetailsStateEventType.SetBottomField;
  name: keyof QuoteDetailsBottomInfo;
  value: any;
};

export type QuoteDetailsStateVariationMoveCreditDebitEvent = {
  type: QuoteDetailsStateEventType.VariationMoveCreditDebit;
  variationId: ID;
};

export type QuoteDetailsStateChangeVariationFilterEvent = {
  type: QuoteDetailsStateEventType.ChangeVariationListFilter;
  variationsFilter: VariationsFilter;
};

export type QuoteDetailsStateNavigateToInvalidEvent = {
  type: QuoteDetailsStateEventType.NavigateToInvalid;
};

export type QuoteDetailsStateNavigateToNotPriceBookVariationEvent = {
  type: QuoteDetailsStateEventType.NavigateToNotPriceBookVariation;
};

export type QuoteDetailsStateResetFocusedCellEvent = {
  type: QuoteDetailsStateEventType.ResetFocusedCell;
};

export type QuoteDetailsStateApprovedEvent = {
  type: QuoteDetailsStateEventType.Approved;
  details: QuoteDetailsInitResponse;
};

export type QuoteDetailsStateUnapprovedEvent = {
  type: QuoteDetailsStateEventType.Unapproved;
  details: QuoteDetailsInitResponse;
};

export type QuoteDetailsStateUpdateVersionEvent = {
  type: QuoteDetailsStateEventType.UpdateVersion;
  version: EntityVersion;
};

export type QuoteDetailsStateSetProfitVisibility = {
  type: QuoteDetailsStateEventType.SetProfitVisibility;
  visible: boolean;
};

export type QuoteDetailsStateEvent = QuoteDetailsStateLoadEvent
  | QuoteDetailsStateMergeOnSaveEvent
  | QuoteDetailsStateMergeOnRecalculateEvent
  | QuoteDetailsStateSetMasterInfoShadowEvent
  | QuoteDetailsStateCommitMasterInfoEvent
  | QuoteDetailsStateVariationAddSetEvent
  | QuoteDetailsStateVariationSetFieldEvent
  | QuoteDetailsStateVariationRemoveEvent
  | QuoteDetailsStateSetBottomFieldEvent
  | QuoteDetailsStateVariationMoveCreditDebitEvent
  | QuoteDetailsStateNavigateToInvalidEvent
  | QuoteDetailsStateNavigateToNotPriceBookVariationEvent
  | QuoteDetailsStateResetFocusedCellEvent
  | QuoteDetailsStateApprovedEvent
  | QuoteDetailsStateUnapprovedEvent
  | QuoteDetailsStateUpdateVersionEvent
  | QuoteDetailsStateSetProfitVisibility
  | QuoteDetailsStateChangeVariationFilterEvent;

export type QuoteDetailsSelection = {
  focusedCell?: FocusedCell;
};

export type QuoteDetailsSettings = {
  isSalesEstimateShown: boolean;
  isQuickQuotesAllowed: boolean;
  isGtoTopExist: boolean;
  useSalesEstimate: boolean;
  supportsProfit: boolean;
  isProfitVisible: boolean;
};

export type VariationsFilter = {
  search: string;
};

export type QuoteDetailsState = WithVersion<WithFormMode<WithRecalculationNeeded<WithDirty<{
  contactId: ID;
  quotationId?: ID;
  derivedInfo: QuoteDetailsDerivedInfo;
  masterInfo: FormStateSnapshot<QuoteDetailsMasterInfo>;
  bottom: QuoteDetailsBottomInfo;
  // shadow is used to store current form state snapshot
  // when form state is applied it is stored in the masterInfo field
  masterInfoShadow: FormStateSnapshot<QuoteDetailsMasterInfo>;
  variations: Table<QuoteVariation>;
  variationsFilter: VariationsFilter;
  selection: QuoteDetailsSelection;
  settings: QuoteDetailsSettings;
  method: QuotationPrintMethod;
}>>>>;

export const QUOTE_DETAILS_INIT_STATE: QuoteDetailsState = {
  contactId: '',
  derivedInfo: <QuoteDetailsDerivedInfo>{},
  bottom: {
    memo: '',
    isMemoOnTop: false,
  },
  masterInfo: <FormStateSnapshot<QuoteDetailsMasterInfo>>{},
  masterInfoShadow: <FormStateSnapshot<QuoteDetailsMasterInfo>>{},
  variations: quoteVariationTable.create(),
  formMode: FormMode.ViewMode,
  isDirty: false,
  isRecalculationNeeded: false,
  version: -1,
  selection: {},
  settings: {
    isSalesEstimateShown: false,
    isQuickQuotesAllowed: false,
    isGtoTopExist: false,
    useSalesEstimate: false,
    supportsProfit: false,
    isProfitVisible: false,
  },
  method: QuotationPrintMethod.PrintQuote,
  variationsFilter: { search: '' },
};

/**
 * Merges sales price and price without GST (for au or nz region) into derived info
 */
export const calculateTopDerivedInfo = (derived: QuoteDetailsDerivedInfo, pbSalesPrice: number = 0, pbPrice = 0) =>
  setEntityFields(derived, { pbSalesPrice, pbPrice });

export const queryQuoteId = (state: QuoteDetailsState) => state.quotationId;
export const queryQuoteContactId = (state: QuoteDetailsState) => state.contactId;
export const queryQuoteDerivedInfo = (state: QuoteDetailsState) => state.derivedInfo;
export const queryQuoteSalesFolderId = (state: QuoteDetailsState) => queryQuoteDerivedInfo(state).salesFolderId;
export const queryQuoteIsProduced = (state: QuoteDetailsState) => isNotNil(queryQuoteDerivedInfo(state).rfqNumber);
export const queryQuoteIsApproved = (state: QuoteDetailsState) => isNotNil(queryQuoteDerivedInfo(state).approvedDate);
export const queryQuoteBottomInfo = (state: QuoteDetailsState) => state.bottom;
export const queryQuoteTopFormHasErrors = (state: QuoteDetailsState) =>
  isCreateFormMode(state.formMode)
  && isNotNil(validateQuoteMasterInfoForCreate(getFormStateSnapshotValue(queryQuoteMasterInfo(state))))
  ||
  isEditFormMode(state.formMode)
  && isNotNil(validateQuoteMasterInfoForEdit(getFormStateSnapshotValue(queryQuoteMasterInfo(state))));
export const queryQuoteInvalidVariations = (state: QuoteDetailsState) =>
  queryQuoteVariations(state).filter(isEntityInvalid);
export const queryQuoteVariationsHasErrors = (state: QuoteDetailsState) =>
  queryQuoteInvalidVariations(state).length > 0;
export const queryQuoteVariationsFilter = (state: QuoteDetailsState) =>
  state.variationsFilter;
export const queryQuoteHasErrors = (state: QuoteDetailsState) =>
  queryQuoteTopFormHasErrors(state) || queryQuoteVariationsHasErrors(state);
export const queryQuoteMasterInfo = (state: QuoteDetailsState) => state.masterInfo;
export const queryQuotationBase = (state: QuoteDetailsState) =>
  getFormStateSnapshotValue(queryQuoteMasterInfo(state)).base;
export const queryQuotePriceBook = (state: QuoteDetailsState) => getFormStateSnapshotValue(state.masterInfo).priceBook;
export const queryQuoteHousePlan = (state: QuoteDetailsState) => getFormStateSnapshotValue(state.masterInfo).housePlan;
export const queryQuoteBaseHousePlan = (state: QuoteDetailsState) =>
  getFormStateSnapshotValue(state.masterInfo).baseHousePlan;
export const queryQuoteMasterInfoShadow = (state: QuoteDetailsState) => state.masterInfoShadow;
export const queryQuoteVariations = (state: QuoteDetailsState) =>
  quoteVariationTable.byOrder(state.variations, 'existing');
export const queryQuoteDirtyVariations = (state: QuoteDetailsState) =>
  quoteVariationTable.all(state.variations).filter(whenModified);
export const queryQuoteVariation = (state: QuoteDetailsState, id: ID) => quoteVariationTable.byId(state.variations, id);
export const queryQuoteVariationFocusedCell = (state: QuoteDetailsState) => state.selection.focusedCell;
export const queryQuoteSettings = (state: QuoteDetailsState) => state.settings;
export const queryQuoteIsProfitSupported = (state: QuoteDetailsState) => queryQuoteSettings(state).supportsProfit;
export const queryQuoteIsProfitVisible = (state: QuoteDetailsState) => queryQuoteSettings(state).isProfitVisible;
export const queryQuoteNotPriceBookVariations = (state: QuoteDetailsState) =>
  queryQuoteVariations(state).filter((variation) => !variation.isInPriceBook);
export const queryQuoteHasNotPriceBookVariations = flow(queryQuoteNotPriceBookVariations, isEmpty);
const queryQuoteSaveOrRecalculateModel = (state: QuoteDetailsState, isPersist: boolean) => {
  const masterInfo = getFormStateSnapshotValue(queryQuoteMasterInfo(state));

  return <QuoteDetailsSaveRequest>{
    quotationId: queryQuoteId(state),
    salesFolderId: queryQuoteSalesFolderId(state),
    priceBookId: masterInfo.priceBook!.id,
    description: masterInfo.description,
    isRounded: masterInfo.isRounded,
    base: masterInfo.base,
    siteId: masterInfo.site && masterInfo.site.id,
    baseHousePlanId: masterInfo.baseHousePlan && masterInfo.baseHousePlan!.housePlanId,
    salesTaxRates: masterInfo.salesTaxRates,
    memo: state.bottom.memo,
    isMemoOnTop: state.bottom.isMemoOnTop,
    version: state.version,
    variations: queryQuoteDirtyVariations(state).map((variation) => ({
      quotationItemId: variation.quotationItemId,
      itemId: variation.itemId,
      isComponent: variation.isComponent,
      description: variation.description,
      costCenterId: variation.costCenterId,
      quantity: variation.quantity,
      origQuantity: variation.origQuantity,
      price: variation.price,
      itemPrice: variation.itemPrice,
      isCredit: variation.isCredit,
      dirtyType: variation.dirtyType,
    })),
    isPersist,
  };
};
export const queryQuoteSaveModel = (state: QuoteDetailsState) => queryQuoteSaveOrRecalculateModel(state, true);
export const queryQuoteRecalculateModel = (state: QuoteDetailsState) => queryQuoteSaveOrRecalculateModel(state, false);
export const queryQuotePrintProduceRequest = (state: QuoteDetailsState,
                                              filter: QuotePrintFilter) => <QuotePrintRequest>({
  salesFolderId: queryQuoteSalesFolderId(state),
  quotationId: queryQuoteId(state)!,
  type: getFormStateSnapshotValue(queryQuoteMasterInfo(state)).base,
  isShowBasePrice: filter.isShowBasePrice,
  isForEstimator: filter.isForEstimator,
  isAuthorityToProceedRequired: filter.isAuthorityToProceedRequired,
  isPrintTakeOff: filter.isPrintTakeOff,
  method: filter.deliveryBy === ContactDeliveryMethod.Email
    ? QuotationPrintMethod.SendByEmail
    : QuotationPrintMethod.PrintQuote,
  version: queryVersion(state),
  entryPoint: PrintEntryPoint.Produce,
});
